import { Component, computed, effect, inject, linkedSignal, OnInit, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzButtonComponent, NzButtonModule } from 'ng-zorro-antd/button';
import { DatePipe } from '@angular/common';
import { NzColDirective, NzGridModule } from 'ng-zorro-antd/grid';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzInputDirective, NzInputModule } from 'ng-zorro-antd/input';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import {
  BloodComponentPipe,
  ClassificationCodeService,
  EhrService,
  EhrTemplateService,
  ErxService,
  QuestionaryBuilderService,
  RecommendationRecordsService,
  TreatmentRecordsService
} from '@beautyapp/core';
import { catchError, filter, of, tap, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { CreateTreatmentComponent } from '../create-treatment/create-treatment.component';
import { NzTreeSelectComponent } from 'ng-zorro-antd/tree-select';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { CreateRecommendationComponent } from '../create-recommendation/create-recommendation.component';
import { EhrCompleteComponent } from '../ehr-complete/ehr-complete.component';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AddErxComponent } from '../add-erx/add-erx.component';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { PatientAnamnesisComponent } from '../patient-anamnesis/patient-anamnesis.component';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzAlertModule } from 'ng-zorro-antd/alert';

@UntilDestroy()
@Component({
  selector: 'beautyapp-ehr-form',
  imports: [
    NzCardModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormDirective,
    NzButtonComponent,
    NzColDirective,
    NzDividerComponent,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzInputDirective,
    NzGridModule,
    NzSelectComponent,
    NzOptionComponent,
    NzCollapseModule,
    NzIconDirective,
    NzTableModule,
    NzSpaceComponent,
    NzCheckboxModule,
    NzSpaceItemDirective,
    NzTreeSelectComponent,
    NzRadioGroupComponent,
    NzRadioComponent,
    DatePipe,
    NzPopconfirmModule,
    TranslatePipe,
    NzSpinModule,
    NzPageHeaderModule,
    NzButtonModule,
    NzInputModule,
    PatientAnamnesisComponent,
    NzTooltipDirective,
    BloodComponentPipe,
    NzAlertModule
],
  templateUrl: './ehr-form.component.html',
  styleUrl: './ehr-form.component.scss'
})
export class EhrFormComponent implements OnInit {
  data = signal(inject(NZ_DRAWER_DATA));
  drawerRef = inject(NzDrawerRef);
  modalService = inject(NzModalService);
  ehrService = inject(EhrService);
  ehrTemplateService = inject(EhrTemplateService);
  erxService = inject(ErxService);
  questionaryBuilderService = inject(QuestionaryBuilderService);
  classificationCodeService = inject(ClassificationCodeService);
  recommendationRecordsService = inject(RecommendationRecordsService);
  treatmentRecordsService = inject(TreatmentRecordsService);
  translateService = inject(TranslateService);
  notificationService = inject(NzNotificationService);
  messageService = inject(NzMessageService);

  initialFormValue: any;


  ehrResources = rxResource({
    params: () => ({ ehrId: this.data().ehrId, isTemplate: this.data().isTemplate, template: this.data().template }),
    stream: ({ params }) => {
      if (params.isTemplate && params.template) {
        // Unpack template content into EHR-like structure
        const content = params.template.content || {};
        return of({
          id: params.template.id,
          ehrNo: null,
          visitDate: null,
          ehrId: null,
          status: null,
          user: null,
          employee: null,
          userId: null,
          visitId: null,
          icd10Id: content.icd10Id || null,
          icd102Id: content.icd102Id || null,
          illness: content.illness || null,
          epicrisis: null,
          typeOfHospitalization: content.typeOfHospitalization || null,
          simptoms: content.simptoms || null,
          diseaseAnamnesis: content.diseaseAnamnesis || null,
          objectiveExaminations: content.objectiveExaminations || null,
          questionnaireBuilderId: content.questionnaires?.id || null,
          questionnaires: content.questionnaires || null,
          diseaseComplications: content.diseaseComplications || [],
          accompanyingDisease: content.accompanyingDisease || [],
          visits: [],
          errors: []
        });
      } else if (params.ehrId) {
        return this.ehrService.findOne(params.ehrId);
      }
      // Return empty structure for new template
      return of({
        id: null,
        ehrNo: null,
        visitDate: null,
        ehrId: null,
        status: null,
        user: null,
        employee: null,
        userId: null,
        visitId: null,
        icd10Id: null,
        icd102Id: null,
        illness: null,
        epicrisis: null,
        typeOfHospitalization: null,
        simptoms: null,
        diseaseAnamnesis: null,
        objectiveExaminations: null,
        questionnaireBuilderId: null,
        questionnaires: null,
        diseaseComplications: [],
        accompanyingDisease: [],
        visits: [],
        errors: []
      });
    }
  });

  treatmentRecordsResource = rxResource({
    params: () => ({ ehrId: this.data().ehrId, isTemplate: this.data().isTemplate }),
    stream: ({ params }) => {
      if (params.isTemplate || !params.ehrId) {
        return of({ data: [] });
      }
      return this.treatmentRecordsService.getAll(params.ehrId);
    }
  });

  recommendationsResource = rxResource({
    params: () => ({ ehrId: this.data().ehrId, isTemplate: this.data().isTemplate }),
    stream: ({ params }) => {
      if (params.isTemplate || !params.ehrId) {
        return of({ data: [] });
      }
      return this.recommendationRecordsService.getAll(params.ehrId);
    }
  });

  prescriptionsResource = rxResource({
    params: () => ({ ehrId: this.data().ehrId, isTemplate: this.data().isTemplate }),
    stream: ({ params }) => {
      if (params.isTemplate || !params.ehrId) {
        return of({ prescriptions: [] });
      }
      return this.erxService.getByEhrId(params.ehrId);
    }
  });

  ehr = computed(() => this.ehrResources.value());
  ehrLoading = computed(() => this.ehrResources.isLoading());
  submitLoading = signal(false);
  isTemplateMode = computed(() => this.data().isTemplate === true);


  visits = linkedSignal(() => this.ehr()?.visits || []);
  erxPrescriptions = computed(() => this.prescriptionsResource.value()?.prescriptions || []);


  treatmentRecords = computed(() => this.treatmentRecordsResource.value()?.data || []);
  localTreatmentRecords = signal<any[]>([]);

  // Combined treatment records - server + local for template mode
  allTreatmentRecords = computed(() => {
    if (this.isTemplateMode()) {
      return [...this.treatmentRecords(), ...this.localTreatmentRecords()];
    }
    return this.treatmentRecords();
  });

  recommendations = computed(() => this.recommendationsResource.value()?.data || []);

  clinicalDiagnosis = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'CLINICAL_DIAGNOSTIC') || []);
  otherActivities = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'OTHER_ACTIVITY') || []);
  labTests = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'LAB_TEST') || []);
  consultations = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'CONSULTATION') || []);
  prescriptions = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'PRESCRIPTION') || []);
  transfusions = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'TRANSFUSION') || []);
  anesthesias = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'ANESTHESIA') || []);
  surgeries = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'SURGERY') || []);
  operationProtocols = linkedSignal(() => this.allTreatmentRecords()?.filter((f: any) => f.type === 'OPERATION_PROTOCOL') || []);


  recomendedSurgicalIntervention = linkedSignal(() => this.recommendations()?.filter((f: any) => f.type === 'SURGICAL') || []);
  instrumentalExplorations = linkedSignal(() => this.recommendations()?.filter((f: any) => f.type === 'INSTRUMENTAL') || []);
  consultationsInAmbulatories = linkedSignal(() => this.recommendations()?.filter((f: any) => f.type === 'CONSULTATION') || []);
  recommendationOther = linkedSignal(() => this.recommendations()?.filter((f: any) => f.type === 'OTHER') || []);


  questionnaires = toSignal(this.questionaryBuilderService.list().pipe(
    map(res => res.data.map(item => ({
      label: item.name,
      value: item.id
    }))),
    catchError(() => of([]))
  ), { initialValue: [] });

  questionnairesFields = signal<any[]>([]);
  questionnairesFormFields = signal<any>({});

  classificationIcdSearch = signal<string>('');


  illnessTypes = signal(this.classificationCodeService.getIllness());

  form: FormGroup = new FormGroup({
    id: new FormControl(null, { validators: [Validators.required], nonNullable: true }),
    visitId: new FormControl<string | null>(null),
    epicrisis: new FormControl<string | null>(null),
    icd10Id: new FormControl<string | null>(null),
    icd102Id: new FormControl<string | null>(null),
    illness: new FormControl<number | null>(null),
    typeOfHospitalization: new FormControl<number | null>(null),
    simptoms: new FormControl<string | null>(null),
    diseaseAnamnesis: new FormControl<string | null>(null),
    questionnaireBuilderId: new FormControl<number | null>(null),
    objectiveExaminations: new FormControl<string | null>(null),
    questionnaires: new FormGroup({}),
    anamnesisVitae: new FormGroup({
      disabilityStatus: new FormControl<string | null>(null),
      diseaseHistory: new FormArray([]),
      surgicalInterventions: new FormArray([]),
      chronicalDiseases: new FormArray([]),
      transfusions: new FormArray([]),
      alergiesDrugs: new FormArray([]),
      otherAllergiesAndReactions: new FormArray([]),
      immunization: new FormArray([]),
      screening: new FormArray([])
    }),
    diseaseComplications: new FormArray([]),
    accompanyingDisease: new FormArray([])
  });

  readonly customStyle = {
    background: '#ffffff',
    'border-radius': '16px',
    'margin-bottom': '16px',
    border: '1px solid #d9d9d9'
  };

  get questionnairesForm() {
    return this.form.get('questionnaires') as FormGroup;
  }


  get diseaseComplications() {
    return this.form.get('diseaseComplications') as FormArray;
  }

  get accompanyingDiseaseForm() {
    return this.form.get('accompanyingDisease') as FormArray;
  }

  medicalSpeciality = (id: number) => {
    return this.classificationCodeService.medicalSpeciality(id);
  };

  constructor() {
    effect(() => {
      if (this.ehr()) {
        this.form.patchValue({
          id: this.ehr().id,
          visitId: this.ehr().visitId,
          icd10Id: this.ehr().icd10Id,
          icd102Id: this.ehr().icd102Id,
          illness: this.ehr().illness,
          epicrisis: this.ehr().epicrisis,
          typeOfHospitalization: this.ehr().typeOfHospitalization,
          simptoms: this.ehr().simptoms,
          diseaseAnamnesis: this.ehr().diseaseAnamnesis,
          objectiveExaminations: this.ehr().objectiveExaminations
        });
        this.form.get('questionnaireBuilderId')?.setValue(this.ehr().questionnaireBuilderId, { emitEvent: false });
        console.log(this.ehr().questionnaires);
        this.questionnairesFormFields.set(this.ehr()?.questionnaires);
        this.questionnairesFields.set(this.ehr()?.questionnaires?.fields);
        if (this.ehr().diseaseComplications.length > 0) {
          this.ehr().diseaseComplications.forEach((dc: { icd10Id: number; illness: number; }) => {
            this.diseaseComplications.push(
              new FormGroup({
                icd10Id: new FormControl<number | null>(dc.icd10Id),
                illness: new FormControl<number | null>(dc.illness)
              })
            );
          });
        }
        if (this.ehr().accompanyingDisease.length > 0) {
          this.ehr().accompanyingDisease.forEach((ad: { icd10Id: number; icd102Id: number; illness: number; }) => {
            this.accompanyingDiseaseForm.push(
              new FormGroup({
                icd10Id: new FormControl<number | null>(ad.icd10Id),
                icd102Id: new FormControl<number | null>(ad.icd102Id),
                illness: new FormControl<number | null>(ad.illness)
              })
            );
          });
        }
        // this.getAnamnesis(this.ehr().userId);
        setTimeout(() => {
          this.initialFormValue = this.form.value;
        }, 1500);

      }
    });

    effect(() => {
      if (this.questionnairesFormFields()) {
        console.log(this.questionnairesFormFields());
        const group: any = {};
        this.questionnairesFormFields()?.fields?.forEach((field: any) => {
          console.log(field);

          group[field.name] = field.required
            ? this.questionnairesForm.addControl(field.name, new FormControl(field.value, [Validators.required]))
            : this.questionnairesForm.addControl(field.name, new FormControl(field.value));

        });
        console.log(group);
      }
    });

    // Load treatment records from template content when in template mode
    effect(() => {
      if (this.isTemplateMode() && this.data().template?.content?.treatmentRecords) {
        const treatmentRecords = this.data().template.content.treatmentRecords;
        if (treatmentRecords && treatmentRecords.length > 0) {
          // Map treatment records and add temporary IDs
          const localRecords = treatmentRecords.map((record: any, index: number) => ({
            ...record,
            id: `temp-${Date.now()}-${index}`,
            isLocal: true
          }));
          this.localTreatmentRecords.set(localRecords);
        }
      }
    });

    if (this.classificationCodeService.classificationICDCodes().length === 0) {
      this.classificationCodeService.getIcdCodes();
    }

    if (this.classificationCodeService.classificationNCSPCodes().length === 0) {
      this.classificationCodeService.getNCSPCodes();
    }
  }

  ngOnInit() {
    this.form.get('questionnaireBuilderId')?.valueChanges
      .pipe(
        switchMap((questionnaireBuilderId) => {
          if (questionnaireBuilderId) {
            return this.getQuestionaryBuilderId(questionnaireBuilderId);
          }
          return of(null);
        })
      )
      .subscribe((value) => {
        console.log(value);
        // remove existing controls
        Object.keys(this.questionnairesForm.controls).forEach((key) => {
          this.questionnairesForm.removeControl(key);
        });

        this.questionnairesFormFields.set(value);
        this.questionnairesFields.set(value?.fields);
      });

    const originalCloseFn = this.drawerRef.close;
    this.drawerRef.close = () => {
      if (this.isFormDirty()) {
        this.modalService.confirm({
          nzTitle: this.translateService.instant('common.unsavedChangesTitle'),
          nzContent: this.translateService.instant('common.unsavedChanges'),
          nzOkText: this.translateService.instant('buttons.save'),
          nzOkType: 'primary',
          nzOnOk: () => {
            // Save form then close
            this.saveEhr(this.ehr().id);
            originalCloseFn.call(this.drawerRef);
          },
          nzCancelText: this.translateService.instant('buttons.decline'),
          nzOnCancel: () => {
            // Close without saving
            originalCloseFn.call(this.drawerRef);
          }
        });
      } else {
        originalCloseFn.call(this.drawerRef);
      }
    };

  }

  getQuestionaryBuilderId(questionnaireBuilderId: number) {
    return this.questionaryBuilderService.get(questionnaireBuilderId);
  }


  treatmentTypes: any[] = [
    {
      value: 1,
      alias: 'გადაუდებელი (კრიტიკული)'
    },
    {
      value: 2,
      alias: 'სასწრაფო დაყოვნებული'
    },
    {
      value: 3,
      alias: 'გეგმიური'
    }
  ];

  createTreatment(ehrId: number) {
    const modalRef = this.modalService.create({
      nzTitle: 'Create',
      nzContent: CreateTreatmentComponent,
      nzData: {
        ehrId: ehrId,
        isTemplateMode: this.isTemplateMode()
      }
    });

    modalRef.afterClose.subscribe((result) => {
      if (result && this.isTemplateMode()) {
        // In template mode, add to local storage
        const newRecord = {
          ...result,
          id: `temp-${Date.now()}-${Math.random()}`, // Generate temporary ID
          isLocal: true
        };
        this.localTreatmentRecords.update(records => [...records, newRecord]);
      } else if (result && !this.isTemplateMode()) {
        // In normal mode, reload from server
        this.treatmentRecordsResource.reload();
      }
    });
  }

  createRecommendation(ehrId: number, data?: any) {
    this.modalService.create({
      nzTitle: 'Create',
      nzContent: CreateRecommendationComponent,
      nzData: {
        ehrId: ehrId,
        data
      },
      nzOnOk: () => {
        this.recommendationsResource.reload();
      }
    });
  }

  editTreatment(type: 'CLINICAL_DIAGNOSTIC' | 'OTHER_ACTIVITY' | 'LAB_TEST' | 'CONSULTATION' | 'PRESCRIPTION' | 'TRANSFUSION' | 'ANESTHESIA' | 'SURGERY' | 'OPERATION_PROTOCOL', ehrId: number, clinicalDiagnos: any) {
    const modalRef = this.modalService.create({
      nzTitle: 'Create',
      nzContent: CreateTreatmentComponent,
      nzData: {
        type,
        ehrId: ehrId,
        data: clinicalDiagnos,
        isTemplateMode: this.isTemplateMode()
      }
    });

    modalRef.afterClose.subscribe((result) => {
      if (result && this.isTemplateMode()) {
        // In template mode, update local storage
        const isLocal = clinicalDiagnos.isLocal;
        if (isLocal) {
          // Update local record
          this.localTreatmentRecords.update(records =>
            records.map(r => r.id === clinicalDiagnos.id ? { ...result, id: clinicalDiagnos.id, isLocal: true } : r)
          );
        }
      } else if (result && !this.isTemplateMode()) {
        // In normal mode, reload from server
        this.treatmentRecordsResource.reload();
      }
    });
  }


  deleteRecomendation(id: number) {
    this.recommendationRecordsService.delete(id).subscribe(() => {
      this.recommendationsResource.reload();
    });

  }

  deleteTreatment(id: any) {
    if (this.isTemplateMode()) {
      // In template mode, check if it's a local record
      const record = this.allTreatmentRecords().find((r: any) => r.id === id);
      if (record?.isLocal) {
        // Remove from local storage
        this.localTreatmentRecords.update(records => records.filter(r => r.id !== id));
      }
    } else {
      // In normal mode, delete from server
      this.treatmentRecordsService.delete(id).subscribe(() => {
        this.treatmentRecordsResource.reload();
      });
    }
  }

  deleteDrug(id: number) {

  }

  addEditDrug(drug?: any) {
    this.modalService.create({
      nzTitle: 'Create',
      nzContent: AddErxComponent,
      nzData: {
        ehrId: this.ehr().id,
        userId: this.ehr().userId,
        employeeId: this.ehr().employeeId,
        prescription: drug
      },
      nzOnOk: () => {
        this.prescriptionsResource.reload();
      }
    });
  }

  saveEhr(id: number) {
    console.log(id, this.form.value);

    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.submitLoading.set(true);

    this.ehrService.update(this.form.value)
      .pipe(
        untilDestroyed(this),
        catchError((res) => {
          console.log(res);
          this.submitLoading.set(false);
          return throwError(() => res);
        })
      )
      .subscribe(() => {
        this.submitLoading.set(false);
        this.initialFormValue = JSON.parse(JSON.stringify(this.form.value));

        this.notificationService.success(
          this.translateService.instant('success'),
          this.translateService.instant('ehr.successfullySaved')
        );
      });
  }


  sendToEhr(ehrId: number) {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.submitLoading.set(true);
    this.messageService.loading('მიმდინარეობს ანკეტის შენახვა', { nzDuration: 0 });

    this.ehrService.update(this.form.value)
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.submitLoading.set(false);
          this.messageService.remove();
        }),
        switchMap(() => this.modalService.create({
          nzTitle: 'გადაგზავნა',
          nzContent: EhrCompleteComponent,
          nzData: {
            type: 'SEND_TO_EHR',
            ehrId: ehrId,
            hospitalizationResult: this.ehr().hospitalizationResult,
            conditionAtTheMoment: this.ehr().conditionAtTheMoment,
            ehrVisibleStatus: this.ehr().ehrVisibleStatus
          }
        }).afterClose.pipe(
          untilDestroyed(this)
        )),
        filter((res) => !!res)
      )
      .subscribe((res) => {
        if (res) {
          this.submitLoading.set(false);
          this.ehrResources.reload();

        }
      });

  }


  download(item: any) {
    this.ehrService.generateAndSaveForm2205(item.id).subscribe(
      (res) => {
        console.log(res);
        if (res && res.url) {
          // Option 1: Open in new tab
          window.open(res.url, '_blank');

          // Option 2: Download directly (alternative approach)
          // const link = document.createElement('a');
          // link.href = res.url;
          // link.download = `Form220-5-${item.id}.pdf`; // Suggest a filename
          // link.target = '_blank';
          // document.body.appendChild(link);
          // link.click();
          // document.body.removeChild(link);
        }

      }
    );
  }


  isFormDirty(): boolean {
    if (!this.initialFormValue) return false;

    // Create deep copies of the form values to compare
    const currentFormValue = JSON.parse(JSON.stringify(this.form.value));
    const initialFormValue = JSON.parse(JSON.stringify(this.initialFormValue));

    // Check if the form is dirty by comparing stringified values
    return JSON.stringify(currentFormValue) !== JSON.stringify(initialFormValue);
  }


  // Add method to handle close with confirmation
  confirmClose() {
    if (this.isFormDirty()) {
      this.modalService.confirm({
        nzTitle: this.translateService.instant('common.unsavedChanges'),
        nzContent: this.translateService.instant('common.unsavedChangesConfirmation'),
        nzOkText: this.translateService.instant('buttons.save'),
        nzOkType: 'primary',
        nzOnOk: () => {
          // Save form then close
          this.saveEhr(this.ehr().id);
        },
        nzCancelText: this.translateService.instant('buttons.decline'),
        nzOnCancel: () => {
          // Close without saving
          this.drawerRef.close();
        }
      });
    } else {
      // No changes, just close
      this.drawerRef.close();
    }
  }

  addDiseaseComplications() {
    this.diseaseComplications.push(
      new FormGroup({
        icd10Id: new FormControl<number | null>(null),
        illness: new FormControl<number | null>(null)
      })
    );
  }

  removeDiseaseComplications(index: number) {
    this.diseaseComplications.removeAt(index);
  }


  addAccompanyingDisease() {
    this.accompanyingDiseaseForm.push(
      new FormGroup({
        icd10Id: new FormControl<number | null>(null),
        icd102Id: new FormControl<number | null>(null),
        illness: new FormControl<number | null>(null)
      })
    );
  }

  removeAccompanyingDisease(index: number) {
    this.accompanyingDiseaseForm.removeAt(index);
  }

  saveTemplate() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.messageService.error('გთხოვთ შეავსოთ ყველა აუცილებელი ველი');
      return;
    }

    this.submitLoading.set(true);

    // Prepare template content from form matching backend DTO structure
    // Map local treatment records to match backend DTO structure
    const treatmentRecords = this.localTreatmentRecords().map(record => {
      const mappedRecord: any = {
        type: record.type,
        icd10Id: record.icd10Id,
        ncspId: record.ncspId,
        procedureStartDate: record.procedureStartDate,
        procedureEndDate: record.procedureEndDate,
        procedureResult: record.procedureResult
      };

      // Add type-specific fields based on treatment type
      if (record.type === 'LAB_TEST') {
        mappedRecord.lab = record.labCodeId;
        mappedRecord.labExamText = record.labExamText;
      } else if (record.type === 'CONSULTATION') {
        mappedRecord.specialtyCode = record.specialtyCode;
        mappedRecord.consultantName = record.consultantName;
        mappedRecord.consultationDate = record.consultationDate;
        mappedRecord.consultationComment = record.consultationComment;
      } else if (record.type === 'PRESCRIPTION') {
        mappedRecord.drugId = record.drugId;
        mappedRecord.drugName = record.drugName;
        mappedRecord.drugCount = record.drugCount;
        mappedRecord.ds = record.ds;
        mappedRecord.wasUsed = record.wasUsed;
      } else if (record.type === 'TRANSFUSION') {
        mappedRecord.bloodComponent = record.bloodComponent;
        mappedRecord.indicationOfTransfusion = record.indicationOfTransfusion;
        mappedRecord.quantity = record.quantity;
        mappedRecord.transfusionComment = record.transfusionComment;
      } else if (record.type === 'ANESTHESIA') {
        mappedRecord.anesthesiaComment = record.anesthesiaComment;
      } else if (record.type === 'SURGERY' || record.type === 'OPERATION_PROTOCOL') {
        mappedRecord.protocolNo = record.protocolNo;
        mappedRecord.protocolText = record.protocolText;
      }

      return mappedRecord;
    });

    const templateContent = {
      typeOfHospitalization: this.form.value.typeOfHospitalization,
      simptoms: this.form.value.simptoms,
      diseaseAnamnesis: this.form.value.diseaseAnamnesis,
      objectiveExaminations: this.form.value.objectiveExaminations,
      questionnaires: this.questionnairesFormFields(),
      icd10Id: this.form.value.icd10Id,
      icd102Id: this.form.value.icd102Id,
      illness: this.form.value.illness,
      diseaseComplications: this.form.value.diseaseComplications || [],
      accompanyingDisease: this.form.value.accompanyingDisease || [],
      treatmentRecords: treatmentRecords,
      recommendations: []
    };

    const template = this.data().template;
    const mode = this.data().mode;

    const operation = mode === 'create'
      ? this.ehrTemplateService.create({
          name: template?.name || 'New Template',
          description: template?.description || '',
          content: templateContent,
          isActive: true,
          isGeneral: false
        })
      : this.ehrTemplateService.update({
          id: template.id,
          name: template.name,
          description: template.description,
          content: templateContent,
          isActive: template.isActive,
          isGeneral: template.isGeneral
        });

    operation
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.submitLoading.set(false);
          this.messageService.success(
            mode === 'create' ? 'შაბლონი წარმატებით შეიქმნა' : 'შაბლონი წარმატებით განახლდა'
          );
          this.drawerRef.close(true);
        },
        error: (error) => {
          this.submitLoading.set(false);
          this.messageService.error(
            mode === 'create' ? 'შაბლონის შექმნა ვერ მოხერხდა' : 'შაბლონის განახლება ვერ მოხერხდა'
          );
          console.error('Save template error:', error);
        }
      });
  }
}
